
import './Sidebar.less';
import React, { useCallback, useContext, useRef, useState } from 'react';
import { ColorPicker, Slider, Col, Button, Row, Alert, Dropdown, message, Tooltip, Modal } from 'antd';
import type { MenuProps } from 'antd';
import { BoldOutlined, DownOutlined, ItalicOutlined, StrikethroughOutlined, UnderlineOutlined } from '@ant-design/icons';
import type { Color } from 'antd/es/color-picker';

import { FileCenterContext, IFileCenterContext } from '@/common/components/FileCenterProvider';
import { canvas2Blob, canvas2Img, collectCanvas, download, isSubscriptionAvailable, isSupportFileSystemHandle, request } from '@/common/utils/utils';
import { fileCenter, FileInfo, FileStatus } from '@/common/services/FileCenter';
import { getDateFormatLabel, menuItems, customColors } from './constants';
import CustomEvents from '@/common/constants/customEvents';
import FileProcess from './FileProcess/FileProcess';
import eventBus from '@/common/services/eventBus';
import URLS from '@/common/constants/url';
import localStorageUtil from '@/common/utils/localStorageUtil';
import FontSelect from '../WorkSpace/components/FontSelect';
import FontSpecialEffects from './FontSpecialEffetcs';
import Position from './Position/Position';
import type { BatchPhotoRecord, User } from '@/common/constants/interfaces';
import { RGBA } from '@/common/constants/interfaces';
import FileFailedRecord from '@/common/services/FileFailedRecord';
import { reportAddDirHandleFailed, reportBatchDetail, reportCancelDirWrite, reportClickBatchBtnAfterVerify, reportNoFreeCount, reportNoPhotos, reportNotEnoughFreeCount, reportStartBatch } from '@/common/utils/report';
import useFileStatus from '../hooks/fileStatusHook';
import { dirHandleCenter } from '@/common/services/DirHandleCenter';

function Sidebar() {
    const refTextArea = useRef<HTMLTextAreaElement>(null);
    const [dateFormat, setDateFormat] = useState('YYYY-MM-DD');
    const [showProcessModal, setShowProcessModal] = useState(false);

    const fileCenterContext = useContext<IFileCenterContext>(FileCenterContext);
    const {
        fileIdList,
        waterMarkTemplate,
        setWaterMarkTemplate,
        fontName,
        setFontName,
        fontSize,
        setFontSize,
        fontColor,
        setFontColor,
        isBold,
        isItalic,
        setIsBold,
        setIsItalic,
        isUnderline,
        setIsUnderline,
        isStrikethrough,
        setIsStrikethrough,
        angel,
        setAngel,
        tran,
        setTran,
        userInfo,
        setUserInfo,
        subscription,
        selectedWatermarkId,
        watermarks,
    }
        = fileCenterContext;

    const fileInfoList = useFileStatus().fileInfoList;

    const handleCloseProcessModal = useCallback(() => {
        setShowProcessModal(false)
    }, [])

    const handleAfterProcessClose = useCallback(async () => {
        // 获取用户信息,主要是为了更新用户剩余的张数
        const data = await request<User>({ url: URLS.GET_USERINFO_PATH, method: 'GET' })
        setUserInfo(data);
        localStorageUtil.setItem('userInfo', data);
    }, [setUserInfo])

    const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setWaterMarkTemplate(e.target.value)
    }, [setWaterMarkTemplate])


    const handleColorChange = useCallback((color: Color) => {
        setFontColor(RGBA.fromHex(color.toHexString()));
    }, [setFontColor])

    const handleBoldClick = useCallback(() => {
        setIsBold(!isBold)
    }, [isBold, setIsBold])

    const handleItalicClick = useCallback(() => {
        setIsItalic(!isItalic)
    }, [isItalic, setIsItalic])

    const handleUnderlineClick = () => {
        setIsUnderline(!isUnderline);
        // 在这里添加逻辑以更新文本的下划线样式
    };

    const handleStrikethroughClick = () => {
        setIsStrikethrough(!isStrikethrough);
        // 在这里添加逻辑以更新文本的删除线样式
    };

    const verify = () => {
        if (!userInfo) {
            // 弹出登录框
            eventBus.emit(CustomEvents.OPEN_LOGIN_PANEL);
            return false
        }

        if (fileIdList.length === 0) {
            message.warning('请先添加图片')
            reportNoPhotos(fileCenter.batchType)
            return false
        }

        if (isSubscriptionAvailable(subscription)) {
            reportClickBatchBtnAfterVerify(fileCenter.batchType)
            return true
        }

        if (userInfo.free_count <= 0) {
            message.warning('免费使用张数已经用完')
            eventBus.emit(CustomEvents.OPEN_VIP_PANEL);
            reportNoFreeCount(userInfo, fileCenter.batchType, fileIdList.length)
            return false
        }

        if (fileIdList.length > userInfo.free_count) {
            message.warning('剩余免费图片处理张数不足')
            eventBus.emit(CustomEvents.OPEN_VIP_PANEL);
            reportNotEnoughFreeCount(userInfo, fileCenter.batchType, fileIdList.length)
            return false
        }

        reportClickBatchBtnAfterVerify(fileCenter.batchType)
        return true
    }

    const handleFinish = async () => {
        const count = fileInfoList.length;
        const successCount = fileInfoList.filter(fileInfo => fileInfo.status === FileStatus.Success).length;
        const failCount = fileInfoList.filter(fileInfo => fileInfo.status === FileStatus.Failure).length;
        // 发送批量处理结果
        const data = await request<BatchPhotoRecord>({ url: URLS.REPORT_BATCH_PHOTOS_PATH, method: 'POST', body: { batch_type: fileCenter.batchType, total_count: count, success_count: successCount, fail_count: failCount } })
        // 更新用户张数
        handleAfterProcessClose();
        // 上报批量处理详情
        reportBatchDetail(data.id, fileCenter.batchType, fileInfoList, watermarks)
    }

    const handleExport = () => {
        fileCenter.batchType = 'export'
        const result = verify()
        if (!result) {
            return
        }
        reportStartBatch(fileCenter.batchType, fileInfoList, watermarks)
        fileCenter.setAllFilStatus(FileStatus.Pending)
        setShowProcessModal(true)
        collectCanvas(fileCenterContext, true,
            async (canvas: HTMLCanvasElement, fileInfo: FileInfo) => {
                const originImageType = fileInfo.getImageType()
                const saveImageType = originImageType === 'unknown' ? 'image/jpeg' : originImageType
                const objectUrl = await canvas2Img(canvas, saveImageType);
                download(objectUrl, fileInfo);
            },
            handleFinish,
        )
    }

    const startReplaceProcess = () => {

        let finishedCount = 0;
        fileCenter.setAllFilStatus(FileStatus.Pending)
        setShowProcessModal(true)
        collectCanvas(fileCenterContext, true,
            async (canvas: HTMLCanvasElement, fileInfo: FileInfo) => {
                const originImageType = fileInfo.getImageType()
                const saveImageType = originImageType === 'unknown' ? 'image/jpeg' : originImageType
                const blob = await canvas2Blob(canvas, saveImageType);
                if (!blob) {
                    fileInfo.setStatus(FileStatus.Failure);
                    finishedCount += 1;
                    fileInfo.fileFailedRecord = new FileFailedRecord(fileInfo, 'canvas2Blob failed');;
                    return
                }
                const fileHandle = fileInfo.fileHandle as any;
                if (!fileHandle) {
                    fileInfo.setStatus(FileStatus.Failure);
                    finishedCount += 1;
                    fileInfo.fileFailedRecord = new FileFailedRecord(fileInfo, 'fileHandle null');;
                    return
                }
                try {
                    // 获取已经授权的目录下该文件的文件句柄
                    const permittedFileHandle = await dirHandleCenter.getFileHandle(fileHandle);
                    const writableStream = await (permittedFileHandle as any).createWritable();
                    // 4. 写入新内容（可以根据需要在原内容上进行修改）
                    await writableStream.write(blob);
                    // 5. 关闭写入流，完成写入操作
                    await writableStream.close();
                    fileInfo.setStatus(FileStatus.Success);
                } catch (e) {
                    console.error('写入文件失败，可能是用户拒绝了浏览器的写入请求', e);
                    fileInfo.setStatus(FileStatus.Failure);
                    fileInfo.fileFailedRecord = new FileFailedRecord(fileInfo, '写文件失败: ' + e);;
                } finally {
                    finishedCount += 1;
                    if (finishedCount === fileInfoList.length) {
                        handleFinish()
                    }
                }
            },
        )

    }

    const handleReplace = () => {
        fileCenter.batchType = 'replace'
        const result = verify()
        if (!result) {
            return
        }

        reportStartBatch(fileCenter.batchType, fileInfoList, watermarks)

        if (dirHandleCenter.dirHandles.length === 0 && fileIdList.length > 1) {
            Modal.confirm({
                title: '目录授权提示',
                content: (
                    <div>
                        <div>您尚未授予图片所在目录的读写权限。是否立即进行目录授权？</div>
                        <div>完成授权后，即可批量替换，取消则结束当前操作</div>
                    </div>
                ),
                okText: '添加授权',
                cancelText: '取消',
                onOk: async () => {
                    try {
                        // 调用目录选择API，添加读写权限
                        const dirHandle = await (window as any).showDirectoryPicker({ mode: 'readwrite' });
                        await dirHandleCenter.addDirHandle(dirHandle);
                        // 授权成功后继续处理
                        startReplaceProcess();
                    } catch (error) {
                        console.error('添加目录授权失败', error);
                        message.error('添加目录授权失败，请重试');
                        reportAddDirHandleFailed(error + '');
                    }
                },
                onCancel: () => {
                    // 用户不添加授权，继续执行
                    reportCancelDirWrite();
                    // startReplaceProcess();
                }
            });
            return;
        }
        startReplaceProcess();

    }

    const handleAddPhotoDate = useCallback(() => {
        if (!refTextArea.current) {
            return
        }
        const textarea = refTextArea.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        console.log('start ', start, 'end ', end)
        const value = waterMarkTemplate.substring(0, start) + `{${dateFormat}}` + waterMarkTemplate.substring(end)
        setWaterMarkTemplate(value)
    }, [waterMarkTemplate, dateFormat, setWaterMarkTemplate])


    const handleMenuClick: MenuProps['onClick'] = (e) => {
        setDateFormat(e.key)
    };

    const menuProps = {
        items: menuItems,
        onClick: handleMenuClick,
        selectedKeys: [dateFormat]
    };

    const isReplaceDisabled = !isSupportFileSystemHandle();

    return (
        <div className='sidebar-container'>
            <div className='setting-container'>
                {!selectedWatermarkId && <div className='sidebar-mask'></div>}
                <div className='line-center'>
                    <div className='text-watermark-title'>文字水印</div>
                </div>
                <div className='setting-group'>
                    <textarea ref={refTextArea} className='line-container sidebar-textarea' placeholder='请输入文字...' rows={3} value={waterMarkTemplate} onChange={handleTextChange} >
                    </textarea>
                    <Dropdown.Button type='dashed' className='line-container add-photo-date-btn' menu={menuProps} onClick={handleAddPhotoDate} icon={<DownOutlined />}>
                        添加拍摄日期({getDateFormatLabel(dateFormat)})
                    </Dropdown.Button>
                    <FontSelect className='line-container font-select' fontName={fontName} onChange={setFontName} />
                    <Row className='font-style-container'>
                        <Col span={6} className='text'>
                            样式
                        </Col>
                        <Col span={18}>
                            <div className='button-group'>
                                <Tooltip placement="bottom" title="设置字体颜色" arrow={true}>
                                    <ColorPicker size='small' value={fontColor.toRgbaString()} presets={customColors} onChange={handleColorChange} />
                                </Tooltip>
                                <Tooltip placement="bottom" title="加粗" arrow={true}>
                                    <Button className='button-group-item' type="text" icon={<BoldOutlined />} onClick={handleBoldClick} style={{ backgroundColor: isBold ? 'white' : 'transparent' }} />
                                </Tooltip>
                                <Tooltip placement="bottom" title="斜体" arrow={true}>
                                    <Button className='button-group-item' type="text" icon={<ItalicOutlined />} onClick={handleItalicClick} style={{ backgroundColor: isItalic ? 'white' : 'transparent' }} />
                                </Tooltip>
                                <Tooltip placement="bottom" title="下划线" arrow={true}>
                                    <Button className='button-group-item' type="text" icon={<UnderlineOutlined />} onClick={handleUnderlineClick} style={{ backgroundColor: isUnderline ? 'white' : 'transparent' }} />
                                </Tooltip>
                                <Tooltip placement="bottom" title="删除线" arrow={true}>
                                    <Button className='button-group-item' type="text" icon={<StrikethroughOutlined />} onClick={handleStrikethroughClick} style={{ backgroundColor: isStrikethrough ? 'white' : 'transparent' }} />
                                </Tooltip>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={6} className='text'>
                            大小
                        </Col>
                        <Col span={18}>
                            <Slider
                                min={6}
                                max={150}
                                step={1}
                                onChange={setFontSize}
                                value={fontSize}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col span={6} className='text'>
                            角度
                        </Col>
                        <Col span={14}>
                            <Slider
                                min={-180}
                                max={180}
                                step={10}
                                onChange={setAngel}
                                value={typeof angel === 'number' ? angel : 0}
                            />
                        </Col>
                        <Col span={4} className='text flex-end'>{angel}°</Col>
                    </Row>
                    <Row>
                        <Col span={6} className='text'>
                            透明度
                        </Col>
                        <Col span={14}>
                            <Slider
                                min={0}
                                max={100}
                                step={1}
                                onChange={setTran}
                                value={tran}
                            />
                        </Col>
                        <Col span={4} className='text flex-end'>{tran}%</Col>
                    </Row>
                    <Position />
                </div>
                <FontSpecialEffects />
            </div>

            <div className='near-bottom'>
                <Button disabled={isReplaceDisabled} size='large' className='near-bottom-btn' onClick={handleReplace}>批量替换</Button>
                <Button size='large' type='primary' onClick={handleExport}>批量导出</Button>
            </div>
            {isReplaceDisabled && <Alert className='alert-warning'
                message="当前浏览器不支持批量替换，请使用最新的chrome、edge浏览器"
                type="warning"
                closable
            />}
            {showProcessModal && <FileProcess showProcessModal={showProcessModal} onProcessClose={handleCloseProcessModal} afterProcessClose={handleAfterProcessClose} />}
        </div>
    )
}

export default React.memo(Sidebar)